<template>
  <div class="row poppins h-100">
    <div class="col-12 h-100">
      <div class="table-wrapper h-100 d-flex flex-column">
        <div class="filter-block">
          <div class="d-flex">
            <SearchInput :searchChange="searchChange" />
            <div class="d-flex ml-2">
              <div
                v-for="status in [ALL_STATUS, ...Object.values(projectStatuses)]"
                :key="status"
                @click="statusChange(status)"
                :class="`status-button ${filter.status === status ? 'status-button-active' : ''}`"
              >
                <div v-if="status !== ALL_STATUS" :class="`mr-1 dot ${status}-status`"></div>
                {{ status | i18n }}
              </div>
            </div>
          </div>
          <div v-if="isCommonAdmin || isSuperUser" class="d-flex">
            <b-dropdown variant="primary" right :text="'Add Project' | i18n" size="lg" toggle-class="pl-4 pr-4">
              <b-dropdown-item @click="goToCreateNewEmptyProject">
                {{ 'create_empty_project' | i18n }}
              </b-dropdown-item>
              <b-dropdown-item @click="goToCreateNewProject">
                {{ 'create_project_from_existing' | i18n }}
              </b-dropdown-item>
            </b-dropdown>
            <b-button
              variant="outline-danger"
              size="lg"
              class="ml-3 pl-4 pr-4 d-flex align-items-center"
              :disabled="!selectedList.length"
              @click="deleteProjects"
            >
              <img :src="deleteIconUrl" class="mr-2" />
              {{ 'Remove Project' | i18n }}
            </b-button>
          </div>
        </div>
        <Table
          :update="getPage"
          :columns="columns"
          :rows="rows"
          :filter="filter"
          :updateFilter="updateFilter"
          :loadingInstance="loadingInstance"
          :setLoadingInstance="setLoadingInstance"
          :selectedList="selectedList"
          :setSelectedList="setSelectedList"
          :expand="true"
          :expandOption="expandOption"
          class="overflow-auto pr-1"
          tableName="projectsTable"
        />
      </div>
    </div>
    <PdfDownloadModal v-if="selectedMilestone" :milestoneId="selectedMilestone.id" :milestoneName="selectedMilestone.name" />
    <HistoryModal v-if="selectedMilestone" :project="{ id: selectedMilestone.id }" />
  </div>
</template>

<script>
import axios from 'axios';
import { BIconPlus, BIconSearch } from 'bootstrap-vue';

import { AuthStorageMixin, DeleteRowMixin, NotificationMixin, ProjectsMixin, AuthMixin } from '../../../../../mixins';
import Table from '../../../common/table';
import SearchInput from '../../../common/search-input';
import api from '../../../../api';
import DeleteIcon from '../../../../../assets/icons/delete.svg';
import PdfDownloadModal from './modals/pdf-download-modal';
import HistoryModal from '../project-chat/components/history-modal';

import { PROJECT_STATUSES } from '../../../../../../../../constants';

let inputInterval = null;
const ALL_STATUS = 'all';

export default {
  title: 'Projects',
  components: { BIconPlus, BIconSearch, Table, SearchInput, PdfDownloadModal, HistoryModal },
  mixins: [AuthStorageMixin, DeleteRowMixin, NotificationMixin, ProjectsMixin, AuthMixin],
  data() {
    return {
      ALL_STATUS: ALL_STATUS,
      deleteIconUrl: DeleteIcon,
      selectedList: [],
      loadingInstance: null,
      selectedProject: null,
      selectedMilestone: null,
      filter: {
        page: 1,
        limit: 10,
        totalCount: 0,
        status: ALL_STATUS,
        sortBy: '',
        sortOrder: '',
        searchQuery: '',
      },
      projectStatuses: PROJECT_STATUSES,
      expandOption: {
        expandable: ({ row, column, rowIndex }) => {
          return true;
        },
        render: ({ row }) => {
          return (
            <div class="milestones">
              {row.milestones.map((milestone) => {
                return (
                  <div class="milestones-box">
                    <div class="milestones-field-box">
                      <div class="milestones-field-name">{this.$translate('Milestone Name')}:</div>
                      <div>{milestone.name}</div>
                    </div>
                    <div class="milestones-field-box">
                      <div class="milestones-field-name">{this.$translate('Template')}:</div>
                      <div>{milestone.template.name}</div>
                    </div>
                    <div class="milestones-field-box">
                      <div class="milestones-field-name">{this.$translate('Leader')}:</div>
                      <div>{milestone.leader || '-'}</div>
                    </div>
                    <div class="milestones-field-box" style="justify-content: flex-end;">
                      {<div class={milestone.isActiveUser ? 'active-user' : 'no-active-user'}></div>}
                      <div
                        class="copy-button"
                        style="margin-right: 15px;"
                        on-click={() => {
                          milestone.isOnSiteSupportOnly ? this.goToOnSiteSupportOnly(row.id, milestone.id) : this.goToProject(milestone.id);
                        }}
                      >
                        {milestone.isOnSiteSupportOnly ? this.$translate('Project details') : this.$translate('Chat')}
                      </div>
                      <div
                        class={`copy-button ${!milestone.pdf ? 'copy-button-disabled' : ''}`}
                        style="margin-right: 15px;"
                        on-click={() => (milestone.pdf ? this.openDownloadPdfModal(milestone) : null)}
                      >
                        {this.$translate('PDF')}
                      </div>
                      <div
                        class={`copy-button ${!milestone.pdf ? 'copy-button-disabled' : ''}`}
                        style="margin-right: 15px;"
                        on-click={() => (milestone.pdf ? this.showHistory(milestone) : null)}
                      >
                        {this.$translate('History')}
                      </div>
                      <div class="copy-button" on-click={() => this.goToProjectMilestoneDetails(row.id, milestone.id)}>
                        {this.$translate('Details')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        },
      },
      columns: [
        {
          field: 'status',
          key: 'status',
          title: '',
          width: 15,
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            const cellData = row[column.field];
            const color = (() => {
              switch (cellData) {
                case PROJECT_STATUSES.ACTIVE:
                  return 'active';
                case PROJECT_STATUSES.FINISHED:
                  return 'finished';
              }
            })();
            return <div class={`status-cell ${color}-status`}></div>;
          },
        },
        {
          field: '',
          key: 'expand',
          title: '',
          type: 'expand',
          width: 10,
        },
        {
          field: 'check',
          key: 'check',
          type: 'checkbox',
          title: '',
          width: 50,
          align: 'center',
        },
        {
          field: 'projectInformation.projectNumber',
          key: 'projectInformation.projectNumber',
          title: this.$translate('Project number'),
          sortBy: '',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return row.projectInformation && (row.projectInformation.projectNumber || '');
          },
        },
        {
          field: 'projectInformation.projectName',
          key: 'projectInformation.projectName',
          title: this.$translate('project_name'),
          sortBy: '',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return row.projectInformation && (row.projectInformation.projectName || '');
          },
        },
        {
          field: 'maschine.maschineNumber',
          key: 'maschine.maschineNumber',
          title: this.$translate('Machine No'),
          sortBy: '',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return row.maschine && (row.maschine.maschineNumber || '');
          },
        },
        {
          field: 'projectInformation.projectDescription',
          key: 'projectInformation.projectDescription',
          title: this.$translate('Description'),
          sortBy: '',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return (
              row.projectInformation && (row.projectInformation.projectDescription ? row.projectInformation.projectDescription.slice(0, 100) : '')
            );
          },
        },
        {
          field: 'projectLeader',
          key: 'projectLeader',
          title: this.$translate('Project leader'),
          sortBy: '',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return row.projectInformation && (row.projectInformation.projectLeader.username || '');
          },
        },
        {
          field: 'purchasingOrder.documentNumber',
          key: 'purchasingOrder.documentNumber',
          title: this.$translate('Document number'),
          sortBy: '',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return row.purchasingOrder && (row.purchasingOrder.documentNumber || '');
          },
        },
        {
          field: 'purchasingOrder.articleNumber',
          key: 'purchasingOrder.articleNumber',
          title: this.$translate('Article number'),
          sortBy: '',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return row.purchasingOrder && (row.purchasingOrder.articleNumber || '');
          },
        },
        {
          field: 'actions',
          key: 'actions',
          title: '',
          width: 50,
          renderBodyCell: ({ row }) => {
            return (
              <div class="action-block">
                {<div class={row.isActiveUser ? 'active-user' : 'no-active-user'}></div>}
                <div class="copy-button" on-click={() => this.goToProjectDetails(row.id)}>
                  {this.$translate('Details')}
                </div>
              </div>
            );
          },
        },
      ],
      rows: [],
    };
  },
  methods: {
    showHistory(milestone) {
      this.selectedMilestone = null;
      setTimeout(() => {
        this.selectedMilestone = milestone;
        setTimeout(() => {
          this.$root.$emit('bv::show::modal', 'history-modal');
        }, 1);
      }, 1);
    },
    updateFilter(f) {
      this.filter = {
        ...this.filter,
        ...f,
      };
    },
    setSelectedList(list) {
      this.selectedList = list;
    },
    setLoadingInstance(loadingInstance) {
      this.loadingInstance = loadingInstance;
    },
    searchChange(e) {
      const v = e.target.value;
      this.filter.searchQuery = v;
      if (inputInterval) {
        clearTimeout(inputInterval);
      }
      inputInterval = setTimeout(() => {
        this.getPage();
      }, 500);
    },
    statusChange(status) {
      this.filter.status = status;
      this.getPage();
    },
    getID() {
      return `${Math.round(Math.random() * 10000)}_${new Date().getTime()}`;
    },
    goToCreateNewProject() {
      this.$router.push({ name: 'new-project', params: { id: this.getID() }, query: { isEmpty: false, isNew: true } });
    },
    goToCreateNewEmptyProject() {
      this.$router.push({ name: 'new-project', params: { id: this.getID() }, query: { isEmpty: true, isNew: true } });
    },
    goToProject(id) {
      this.$router.push({ name: 'project', params: { id } });
    },
    goToOnSiteSupportOnly(id, milestoneId) {
      this.$router.push({ name: 'project-details-on-site-support-only', params: { id, milestoneId } });
    },
    goToProjectDetails(id) {
      this.$router.push({ name: 'project-details', params: { id } });
    },
    goToProjectMilestoneDetails(id, milestoneId) {
      this.$router.push({ name: 'milestone', params: { id, milestoneId } });
    },
    async deleteProjects() {
      try {
        await this.deleteSelectedRows({}, async () => {
          const ids = this.selectedList.map((r) => r.id);
          await api.projects.delete({ ids });
          this.rows = this.rows.filter((i) => !ids.includes(i.id));
          this.selectedList = [];
          this.getPage();
        });
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
    startLoading() {
      this.loadingInstance.show();
    },
    stopLoading() {
      this.loadingInstance.close();
    },
    async getPage() {
      try {
        this.startLoading();
        const { page, limit, sortBy, sortOrder, searchQuery, status } = this.filter;
        const ctx = {
          page,
          limit,
          sortBy,
          sortOrder,
          searchQuery,
        };
        if (status && status !== ALL_STATUS) {
          ctx.status = status;
        }
        const response = await api.projects.get(ctx);
        this.filter.totalCount = response.totalCount;
        this.rows = response.data;
        this.stopLoading();
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
    openDownloadPdfModal(milestone) {
      this.selectedMilestone = null;
      setTimeout(() => {
        this.selectedMilestone = milestone;
        setTimeout(() => {
          this.$root.$emit('bv::show::modal', `download-pdf-milestone`);
        }, 1);
      }, 1);
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ve-table-body {
  ::v-deep th {
    color: rgb(112, 112, 112) !important;
    background-color: rgb(239, 239, 239) !important;
    border: none !important;
    min-height: 50px;
  }
  ::v-deep td {
    color: rgb(112, 112, 112) !important;
    cursor: pointer;
  }
}

::v-deep .ve-table-cell-selection {
  border: none !important;
}

::v-deep .ve-table-sort {
  top: -4.5px;
}

::v-deep .chat-action {
  background: rgb(34, 129, 254);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  margin: auto;
}

.statuses-block {
  display: flex;
  justify-content: flex-end;
}

.status-block {
  display: flex;
  margin-left: 20px;
  align-content: center;
  justify-content: center;
  align-items: center;
}

.status-name {
  margin-left: 10px;
  font-size: 14px;
}

.date-range-block {
  min-width: 350px;
  text-align: center;
  margin-left: 30px;
  display: flex;
  align-items: center;
}

::v-deep .ve-select-selected-span {
  font-weight: 400;
}

.pagination-block {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
}

::v-deep .copy-button {
  width: max-content;
  background: rgb(237, 248, 254);
  border: 2px solid rgb(175, 216, 255);
  border-radius: 4px;
  padding: 2px 13px;
  color: rgb(49, 141, 255);
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  cursor: pointer;
}

::v-deep .copy-button-disabled {
  opacity: 0.5;
  cursor: default;
}

.action-block {
  display: flex;
  flex-direction: row;
  align-items: center;
}

::v-deep .status-cell {
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  border-radius: 0 2px 2px 0;
}

.status-button {
  border: 1px solid #c5d0de;
  padding: 8px 16px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 14px;
  line-height: 16px;
  color: rgba(100, 106, 135, 0.6);
  display: flex;
  align-items: center;
  &:first-child {
    border-radius: 4px 0px 0px 4px;
  }
  &:last-child {
    border-radius: 0px 4px 4px 0px;
  }
}

.status-button-active {
  color: #2260ff;
  background: rgba(34, 96, 255, 0.1);
  border: 1px solid #2260ff;
}

::v-deep .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
::v-deep .active-status {
  background-color: rgb(37, 154, 43);
}
::v-deep .inprogress-status {
  background-color: rgb(239, 255, 15);
}
::v-deep .finished-status {
  background-color: rgb(90, 98, 107);
}

.milestones {
  margin-left: 45px;
}

.milestones-box {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin: 15px 0px;
}

.milestones-field-box {
  display: flex;
  flex: 1;
  align-items: center;
}

.milestones-field-name {
  font-weight: bold;
  margin-right: 10px;
}

::v-deep .dropdown-toggle::after {
  margin-left: 10px;
}

::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}

.active-user {
  background: #34ff4a;
  border-radius: 50%;
  width: 10px;
  height: 10px;
  margin-right: 20px;
}

.no-active-user {
  border-radius: 50%;
  width: 10px;
  height: 10px;
  margin-right: 20px;
}
</style>
